import { useTranslation } from "react-i18next";
import { Text } from "@core/text";
import {
  Database,
  CheckCircle,
  BookOpen,
  Target,
  Users,
  Lightbulb,
  Heart,
  Globe,
  Link,
  Puzzle,
  Star,
  Award,
} from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { useGetMaarifLearningOutcomes } from "@packages/api/madlenAPI";
import { GetTopicsCourseType } from "@packages/api/madlenAPI.schemas";
import { CourseData } from "@packages/constant/CourseData";

interface UnitData {
  alanBecerileri?: string;
  anahtarKavramlar?: string;
  becerilerArasiIliskiler?: string;
  degerler?: string;
  destekleme?: string;
  disiplinlerArasiIliskiler?: string;
  egilimler?: string;
  icerikCercevesiList?: string[];
  kavramsalBeceriler?: string;
  kopruKurma?: string;
  okuryazarlikBecerileri?: string;
  onDegerlendirmeSureci?: string;
  sosyalDuygusalOgrenmeBecerileri?: string;
  temelKabuller?: string;
  zenginlestirme?: string;
  ogrenmeOgretmeUygulamalariList?: string[];
}

interface UbdMaarifDataStepProps {
  gradeLevel: number;
  lesson: string;
  selectedOutcomes: { id: string; text: string }[];
  selectedComponents?: {
    alanBecerileri: boolean;
    kavramsalBeceriler: boolean;
    egilimler: boolean;
    sosyalDuygusalOgrenme: boolean;
    degerler: boolean;
    okuryazarlikBecerileri: boolean;
    disiplinlerArasiIliskiler: boolean;
    becerilerArasiIliskiler: boolean;
  };
  onComponentSelectionChange?: (components: {
    alanBecerileri: boolean;
    kavramsalBeceriler: boolean;
    egilimler: boolean;
    sosyalDuygusalOgrenme: boolean;
    degerler: boolean;
    okuryazarlikBecerileri: boolean;
    disiplinlerArasiIliskiler: boolean;
    becerilerArasiIliskiler: boolean;
  }) => void;
  onTymmDataChange?: (tymmData: {
    temelKabuller: string;
    onDegerlendirme: string;
    koprukurma: string;
    zenginlestirme: string;
    destekleme: string;
    ogrenmeOgretmeUygulamalari: string[];
  }) => void;
}

const UbdMaarifDataStep: React.FC<UbdMaarifDataStepProps> = ({
  gradeLevel,
  lesson,
  selectedOutcomes,
  selectedComponents: propSelectedComponents,
  onComponentSelectionChange,
  onTymmDataChange,
}) => {
  const { t } = useTranslation();

  // Default değerler (hepsi seçili)
  const defaultSelectedComponents = {
    alanBecerileri: true,
    kavramsalBeceriler: true,
    egilimler: true,
    sosyalDuygusalOgrenme: true,
    degerler: true,
    okuryazarlikBecerileri: true,
    disiplinlerArasiIliskiler: true,
    becerilerArasiIliskiler: true,
  };

  // Parent'tan gelen prop varsa onu kullan, yoksa local state
  const [localSelectedComponents, setLocalSelectedComponents] = useState(
    defaultSelectedComponents,
  );
  const selectedComponents = propSelectedComponents || localSelectedComponents;

  const handleComponentToggle = (
    componentKey: keyof typeof selectedComponents,
  ) => {
    const newSelection = {
      ...selectedComponents,
      [componentKey]: !selectedComponents[componentKey],
    };

    if (onComponentSelectionChange) {
      onComponentSelectionChange(newSelection);
    } else {
      setLocalSelectedComponents(newSelection);
    }
  };

  // Fetch real Maarif data from API
  const { data: maarifOutcomesData } = useGetMaarifLearningOutcomes({
    grade: gradeLevel,
    courseType: lesson as GetTopicsCourseType,
  });

  // Get units that contain selected learning outcomes
  const selectedUnits = useMemo(() => {
    return maarifOutcomesData?.units?.filter((unit) =>
      unit.outcomes?.find((outcome) =>
        selectedOutcomes
          .map((a) => a.id)
          .includes(outcome.id?.toString() || ""),
      ),
    );
  }, [maarifOutcomesData, selectedOutcomes]);

  // Function to combine column data from selected units
  const getCombinedColumnData = (columnKey: keyof UnitData): string[] => {
    if (!selectedUnits?.length) return [];

    return selectedUnits
      .reduce<string[]>((acc, unit) => {
        const value = unit[columnKey];
        if (Array.isArray(value)) {
          return [...acc, ...value.filter(Boolean)];
        }
        if (value) {
          return [...acc, value];
        }
        return acc;
      }, [])
      .filter(Boolean);
  };

  // Create maarif data from selected units
  const maarifData = useMemo(() => {
    return {
      alanBecerileri: getCombinedColumnData("alanBecerileri"),
      becerilerArasiIliskiler: getCombinedColumnData("becerilerArasiIliskiler"),
      destekleme: getCombinedColumnData("destekleme").join(" "),
      egilimler: getCombinedColumnData("egilimler"),
      icerikCercevesi: getCombinedColumnData("icerikCercevesiList"),
      okuryazarlikBecerileri: getCombinedColumnData("okuryazarlikBecerileri"),
      sosyalDuygusalOgrenme: getCombinedColumnData(
        "sosyalDuygusalOgrenmeBecerileri",
      ),
      koprukurma: getCombinedColumnData("kopruKurma").join(" "),
      zenginlestirme: getCombinedColumnData("zenginlestirme").join(" "),
      anahtarKavramlar: getCombinedColumnData("anahtarKavramlar").join(", "),
      degerler: getCombinedColumnData("degerler"),
      disiplinlerArasiIliskiler: getCombinedColumnData(
        "disiplinlerArasiIliskiler",
      ),
      kavramsalBeceriler: getCombinedColumnData("kavramsalBeceriler"),
      onDegerlendirme: getCombinedColumnData("onDegerlendirmeSureci").join(" "),
      temelKabuller: getCombinedColumnData("temelKabuller").join(" "),
    };
  }, [selectedUnits]);

  // Call TYMM data change callback when maarif data changes
  useEffect(() => {
    if (onTymmDataChange) {
      onTymmDataChange({
        temelKabuller: maarifData.temelKabuller,
        onDegerlendirme: maarifData.onDegerlendirme,
        koprukurma: maarifData.koprukurma,
        zenginlestirme: maarifData.zenginlestirme,
        destekleme: maarifData.destekleme,
        ogrenmeOgretmeUygulamalari: getCombinedColumnData(
          "ogrenmeOgretmeUygulamalariList",
        ),
      });
    }
  }, [maarifData, onTymmDataChange]);

  // Get translated course name
  const getCourseName = () => {
    const courseInfo = CourseData[lesson as keyof typeof CourseData];
    if (courseInfo) {
      return t(`courseType.${courseInfo.name(gradeLevel)}`);
    }
    return lesson;
  };

  const SelectableDataSection: React.FC<{
    title: string;
    items: string | string[];
    isArray?: boolean;
    color?: string;
    icon?: React.ElementType;
    isSelectable?: boolean;
    isSelected?: boolean;
    onToggle?: () => void;
    componentKey?: keyof typeof selectedComponents;
  }> = ({
    title,
    items,
    isArray = Array.isArray(items),
    color = "blue",
    icon: Icon = Star,
    isSelectable = false,
    isSelected = true,
    onToggle,
  }) => {
    // Don't render if data is empty
    if (isArray && (!items || (items as string[]).length === 0)) return null;
    if (!isArray && (!items || (items as string).trim() === "")) return null;

    // Always render, just apply different styles based on selection
    return (
      <DataSection
        title={title}
        items={items}
        isArray={isArray}
        color={color}
        icon={Icon}
        isSelectable={isSelectable}
        isSelected={isSelected}
        onToggle={onToggle}
      />
    );
  };

  const DataSection: React.FC<{
    title: string;
    items: string | string[];
    isArray?: boolean;
    color?: string;
    icon?: React.ElementType;
    isSelectable?: boolean;
    isSelected?: boolean;
    onToggle?: () => void;
  }> = ({
    title,
    items,
    isArray = Array.isArray(items),
    color = "blue",
    icon: Icon = Star,
    isSelectable = false,
    isSelected = true,
    onToggle,
  }) => {
    // Don't render if data is empty
    if (isArray && (!items || (items as string[]).length === 0)) return null;
    if (!isArray && (!items || (items as string).trim() === "")) return null;

    const colorThemes = {
      blue: {
        gradient:
          "from-blue-500/10 via-blue-50/80 to-blue-100/50 dark:from-blue-900/30 dark:via-blue-800/20 dark:to-blue-900/10",
        border: "border-blue-200/60 dark:border-blue-600/30",
        iconBg: "bg-gradient-to-br from-blue-500 to-blue-600",
        title: "text-blue-800 dark:text-blue-200",
        text: "text-blue-700 dark:text-blue-300",
        tag: "bg-gradient-to-r from-blue-500/10 to-blue-600/10 text-blue-700 dark:text-blue-300 border border-blue-200/40 dark:border-blue-600/30",
      },
      purple: {
        gradient:
          "from-purple-500/10 via-purple-50/80 to-purple-100/50 dark:from-purple-900/30 dark:via-purple-800/20 dark:to-purple-900/10",
        border: "border-purple-200/60 dark:border-purple-600/30",
        iconBg: "bg-gradient-to-br from-purple-500 to-purple-600",
        title: "text-purple-800 dark:text-purple-200",
        text: "text-purple-700 dark:text-purple-300",
        tag: "bg-gradient-to-r from-purple-500/10 to-purple-600/10 text-purple-700 dark:text-purple-300 border border-purple-200/40 dark:border-purple-600/30",
      },
      orange: {
        gradient:
          "from-orange-500/10 via-orange-50/80 to-orange-100/50 dark:from-orange-900/30 dark:via-orange-800/20 dark:to-orange-900/10",
        border: "border-orange-200/60 dark:border-orange-600/30",
        iconBg: "bg-gradient-to-br from-orange-500 to-orange-600",
        title: "text-orange-800 dark:text-orange-200",
        text: "text-orange-700 dark:text-orange-300",
        tag: "bg-gradient-to-r from-orange-500/10 to-orange-600/10 text-orange-700 dark:text-orange-300 border border-orange-200/40 dark:border-orange-600/30",
      },
      emerald: {
        gradient:
          "from-emerald-500/10 via-emerald-50/80 to-emerald-100/50 dark:from-emerald-900/30 dark:via-emerald-800/20 dark:to-emerald-900/10",
        border: "border-emerald-200/60 dark:border-emerald-600/30",
        iconBg: "bg-gradient-to-br from-emerald-500 to-emerald-600",
        title: "text-emerald-800 dark:text-emerald-200",
        text: "text-emerald-700 dark:text-emerald-300",
        tag: "bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200/40 dark:border-emerald-600/30",
      },
      pink: {
        gradient:
          "from-pink-500/10 via-pink-50/80 to-pink-100/50 dark:from-pink-900/30 dark:via-pink-800/20 dark:to-pink-900/10",
        border: "border-pink-200/60 dark:border-pink-600/30",
        iconBg: "bg-gradient-to-br from-pink-500 to-pink-600",
        title: "text-pink-800 dark:text-pink-200",
        text: "text-pink-700 dark:text-pink-300",
        tag: "bg-gradient-to-r from-pink-500/10 to-pink-600/10 text-pink-700 dark:text-pink-300 border border-pink-200/40 dark:border-pink-600/30",
      },
      indigo: {
        gradient:
          "from-indigo-500/10 via-indigo-50/80 to-indigo-100/50 dark:from-indigo-900/30 dark:via-indigo-800/20 dark:to-indigo-900/10",
        border: "border-indigo-200/60 dark:border-indigo-600/30",
        iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-600",
        title: "text-indigo-800 dark:text-indigo-200",
        text: "text-indigo-700 dark:text-indigo-300",
        tag: "bg-gradient-to-r from-indigo-500/10 to-indigo-600/10 text-indigo-700 dark:text-indigo-300 border border-indigo-200/40 dark:border-indigo-600/30",
      },
      teal: {
        gradient:
          "from-teal-500/10 via-teal-50/80 to-teal-100/50 dark:from-teal-900/30 dark:via-teal-800/20 dark:to-teal-900/10",
        border: "border-teal-200/60 dark:border-teal-600/30",
        iconBg: "bg-gradient-to-br from-teal-500 to-teal-600",
        title: "text-teal-800 dark:text-teal-200",
        text: "text-teal-700 dark:text-teal-300",
        tag: "bg-gradient-to-r from-teal-500/10 to-teal-600/10 text-teal-700 dark:text-teal-300 border border-teal-200/40 dark:border-teal-600/30",
      },
      amber: {
        gradient:
          "from-amber-500/10 via-amber-50/80 to-amber-100/50 dark:from-amber-900/30 dark:via-amber-800/20 dark:to-amber-900/10",
        border: "border-amber-200/60 dark:border-amber-600/30",
        iconBg: "bg-gradient-to-br from-amber-500 to-amber-600",
        title: "text-amber-800 dark:text-amber-200",
        text: "text-amber-700 dark:text-amber-300",
        tag: "bg-gradient-to-r from-amber-500/10 to-amber-600/10 text-amber-700 dark:text-amber-300 border border-amber-200/40 dark:border-amber-600/30",
      },
      green: {
        gradient:
          "from-green-500/10 via-green-50/80 to-green-100/50 dark:from-green-900/30 dark:via-green-800/20 dark:to-green-900/10",
        border: "border-green-200/60 dark:border-green-600/30",
        iconBg: "bg-gradient-to-br from-green-500 to-green-600",
        title: "text-green-800 dark:text-green-200",
        text: "text-green-700 dark:text-green-300",
        tag: "bg-gradient-to-r from-green-500/10 to-green-600/10 text-green-700 dark:text-green-300 border border-green-200/40 dark:border-green-600/30",
      },
    };

    const theme =
      colorThemes[color as keyof typeof colorThemes] || colorThemes.blue;

    // Enhanced styles based on selection state
    const getCardStyles = () => {
      if (!isSelectable) {
        return "hover:scale-[1.02] transition-all duration-300 hover:shadow-xl";
      }

      if (isSelected) {
        return `
          hover:scale-[1.02] transition-all duration-300 hover:shadow-xl 
          ring-2 ring-${color}-400/50 shadow-lg shadow-${color}-500/20
          cursor-pointer hover:ring-${color}-500/70 hover:shadow-${color}-500/30
          border-solid hover:border-${color}-400
        `;
      } else {
        return `
          opacity-60 hover:opacity-80 scale-[0.98] hover:scale-[1.01] 
          transition-all duration-500 cursor-pointer 
          grayscale hover:grayscale-0 border-dashed border-gray-300 dark:border-gray-600
          hover:border-gray-400 dark:hover:border-gray-500 hover:shadow-md
        `;
      }
    };

    const getIconStyles = () => {
      if (!isSelectable) return theme.iconBg;

      if (isSelected) {
        return `${theme.iconBg} ring-2 ring-white/50`;
      } else {
        return `bg-gray-400 dark:bg-gray-600`;
      }
    };

    const getTitleStyles = () => {
      if (!isSelectable) return theme.title;

      if (isSelected) {
        return theme.title;
      } else {
        return "text-gray-400 dark:text-gray-500";
      }
    };

    const getContentStyles = () => {
      if (!isSelectable) return theme.text;

      if (isSelected) {
        return theme.text;
      } else {
        return "text-gray-400 dark:text-gray-500";
      }
    };

    const getTagStyles = () => {
      if (!isSelectable) return theme.tag;

      if (isSelected) {
        return theme.tag;
      } else {
        return "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-600";
      }
    };

    return (
      <div
        className={`
          relative group overflow-hidden rounded-2xl border ${theme.border} backdrop-blur-sm
          ${getCardStyles()}
        `}
        onClick={isSelectable ? onToggle : undefined}
      >
        {/* Selection indicator */}
        {isSelectable && isSelected && (
          <div className="absolute top-4 right-4 z-10">
            <div
              className={`w-8 h-8 rounded-full bg-gradient-to-br ${theme.iconBg} flex items-center justify-center shadow-xl ring-2 ring-white/50 dark:ring-gray-800/50 animate-pulse`}
            >
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
          </div>
        )}

        {/* Unselected indicator */}
        {isSelectable && !isSelected && (
          <div className="absolute top-4 right-4 z-10 opacity-30 group-hover:opacity-60 transition-opacity duration-300">
            <div className="w-8 h-8 rounded-full bg-gray-400 dark:bg-gray-600 flex items-center justify-center shadow-lg border-2 border-dashed border-gray-300 dark:border-gray-500">
              <div className="w-3 h-3 rounded-full border-2 border-gray-300 dark:border-gray-400" />
            </div>
          </div>
        )}

        {/* Background gradient */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${isSelectable && !isSelected ? "from-gray-100/50 to-gray-200/50 dark:from-gray-700/30 dark:to-gray-800/30" : theme.gradient}`}
        />

        {/* Pattern overlay for unselected items */}
        {isSelectable && !isSelected && (
          <div
            className="absolute inset-0 opacity-20 dark:opacity-10"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.1) 1px, transparent 0)`,
              backgroundSize: "16px 16px",
            }}
          />
        )}

        {/* Content */}
        <div className="relative p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`p-2.5 rounded-xl shadow-lg transition-all duration-300 ${getIconStyles()}`}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <Text
                variant="h3"
                className={`text-sm font-bold tracking-wide transition-colors duration-300 ${getTitleStyles()}`}
              >
                {title}
              </Text>
              {isSelectable && (
                <Text
                  variant="p"
                  size="xs"
                  className="text-xs text-gray-400 dark:text-gray-500 mt-1"
                >
                  {isSelected
                    ? "✓ Planlamada kullanılacak"
                    : "Planlamada kullanılmayacak"}
                </Text>
              )}
            </div>
          </div>

          {/* Content */}
          {isArray ? (
            <div className="flex flex-wrap gap-2">
              {(items as string[]).map((item, index) => (
                <span
                  key={index}
                  className={`
                    px-3 py-1.5 rounded-lg text-xs font-medium backdrop-blur-sm 
                    transition-all duration-300 
                    ${getTagStyles()}
                  `}
                >
                  {item}
                </span>
              ))}
            </div>
          ) : (
            <Text
              variant="p"
              size="sm"
              className={`leading-relaxed font-medium transition-colors duration-300 ${getContentStyles()}`}
            >
              {items as string}
            </Text>
          )}
        </div>

        {/* Click hint overlay for unselected items */}
        {isSelectable && !isSelected && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent dark:from-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
            <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl px-6 py-3 border border-gray-200/70 dark:border-gray-600/70 shadow-xl transform scale-95 group-hover:scale-100 transition-transform duration-300">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <Text
                  variant="p"
                  size="sm"
                  className="text-gray-700 dark:text-gray-200 font-semibold"
                >
                  Tıklayarak planlamaya ekle
                </Text>
                <div
                  className="w-2 h-2 bg-green-500 rounded-full animate-pulse"
                  style={{ animationDelay: "0.5s" }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 dark:from-gray-900/50 dark:via-gray-800 dark:to-indigo-900/30 p-4 space-y-4">
      {/* Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 via-blue-500/10 to-purple-500/20 rounded-3xl blur-xl" />
        <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-2xl rounded-3xl p-4 border border-white/50 dark:border-gray-700/50 shadow-2xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500 via-blue-500 to-purple-600 rounded-xl shadow-xl">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <Text
                variant="h1"
                className="text-xl font-bold bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 bg-clip-text text-transparent"
              >
                Öğrenme Çıktıları ve Süreç Bileşenleri
              </Text>
              <Text
                variant="p"
                className="text-gray-500 dark:text-gray-400 font-medium mt-1"
              >
                {gradeLevel}. Sınıf • {getCourseName()}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Learning Outcomes */}
      {selectedOutcomes.length > 0 && (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-indigo-500/10 rounded-3xl blur-xl" />
          <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-2xl rounded-3xl p-4 border border-white/50 dark:border-gray-700/50 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <Text
                  variant="h2"
                  className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"
                >
                  Seçilen Öğrenme Çıktıları
                </Text>
                <Text
                  variant="p"
                  size="sm"
                  className="text-gray-500 dark:text-gray-400 font-medium"
                >
                  Bir önceki adımda seçtiğiniz {selectedOutcomes.length} öğrenme
                  çıktısı
                </Text>
              </div>
            </div>
            <div className="grid gap-4">
              {selectedOutcomes.map((outcome, index) => (
                <div
                  key={outcome.id}
                  className="group relative overflow-hidden bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200/60 dark:border-blue-600/30 rounded-xl p-3 hover:scale-[1.01] transition-all duration-300"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                      <span className="text-xs font-bold text-white">
                        {index + 1}
                      </span>
                    </div>
                    <Text
                      variant="p"
                      className="text-gray-700 dark:text-gray-300 leading-relaxed flex-1 font-medium"
                    >
                      {outcome.text}
                    </Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Component Selection Info */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/5 to-purple-500/5 rounded-3xl blur-xl" />
        <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-2xl rounded-3xl p-4 border border-white/50 dark:border-gray-700/50 shadow-xl">
          <div className="text-center">
            <Text
              variant="h2"
              className="text-base font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2"
            >
              UbD planınızda kullanmak istediğiniz Programlar Arası Bileşenleri
              seçiniz
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-500 dark:text-gray-400 font-medium mb-3"
            >
              💡 Kartlara tıklayarak seçim yapabilirsiniz
            </Text>

            {/* Selection Summary */}
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-700/50">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <Text
                  variant="p"
                  size="sm"
                  className="text-blue-700 dark:text-blue-300 font-medium"
                >
                  {Object.values(selectedComponents).filter(Boolean).length}/8
                  seçili
                </Text>
              </div>

              {Object.values(selectedComponents).filter(Boolean).length ===
              8 ? (
                <div className="flex items-center gap-2 px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700/50">
                  <span className="text-green-600 dark:text-green-400 text-sm">
                    ✅ Tümü seçili
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-700/50">
                  <span className="text-amber-600 dark:text-amber-400 text-sm">
                    ⚠️ Kısmi seçim
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* İçerik Çerçevesi ve Anahtar Kavramlar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DataSection
          title="İçerik Çerçevesi"
          items={maarifData.icerikCercevesi}
          color="purple"
          icon={BookOpen}
        />
        <DataSection
          title="Anahtar Kavramlar"
          items={maarifData.anahtarKavramlar}
          isArray={false}
          color="blue"
          icon={Lightbulb}
        />
      </div>

      {/* Temel Bilgiler */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-400/10 to-amber-500/10 rounded-3xl blur-xl" />
        <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-2xl rounded-3xl p-8 border border-white/50 dark:border-gray-700/50 shadow-xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl shadow-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <Text
                variant="h2"
                className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent"
              >
                Temel Bilgiler
              </Text>
              <Text
                variant="p"
                size="sm"
                className="text-gray-500 dark:text-gray-400 font-medium"
              >
                Ders ile ilgili temel beceri ve kavramlar
              </Text>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <SelectableDataSection
              title="Alan Becerileri"
              items={maarifData.alanBecerileri}
              color="orange"
              icon={Award}
              isSelectable={true}
              isSelected={selectedComponents.alanBecerileri}
              onToggle={() => handleComponentToggle("alanBecerileri")}
            />
            <SelectableDataSection
              title="Kavramsal Beceriler"
              items={maarifData.kavramsalBeceriler}
              color="amber"
              icon={Puzzle}
              isSelectable={true}
              isSelected={selectedComponents.kavramsalBeceriler}
              onToggle={() => handleComponentToggle("kavramsalBeceriler")}
            />
            <SelectableDataSection
              title="Eğilimler"
              items={maarifData.egilimler}
              color="orange"
              icon={Star}
              isSelectable={true}
              isSelected={selectedComponents.egilimler}
              onToggle={() => handleComponentToggle("egilimler")}
            />
          </div>
        </div>
      </div>

      {/* Programlar Arası Bileşenler */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/10 to-teal-500/10 rounded-3xl blur-xl" />
        <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-2xl rounded-3xl p-8 border border-white/50 dark:border-gray-700/50 shadow-xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-lg">
              <Link className="w-6 h-6 text-white" />
            </div>
            <div>
              <Text
                variant="h2"
                className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"
              >
                Programlar Arası Bileşenler
              </Text>
              <Text
                variant="p"
                size="sm"
                className="text-gray-500 dark:text-gray-400 font-medium"
              >
                Farklı disiplinler arası bağlantılar ve beceriler
              </Text>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <SelectableDataSection
              title="Sosyal-Duygusal Öğrenme"
              items={maarifData.sosyalDuygusalOgrenme}
              color="pink"
              icon={Heart}
              isSelectable={true}
              isSelected={selectedComponents.sosyalDuygusalOgrenme}
              onToggle={() => handleComponentToggle("sosyalDuygusalOgrenme")}
            />
            <SelectableDataSection
              title="Değerler"
              items={maarifData.degerler}
              color="indigo"
              icon={Star}
              isSelectable={true}
              isSelected={selectedComponents.degerler}
              onToggle={() => handleComponentToggle("degerler")}
            />
            <SelectableDataSection
              title="Okuryazarlık Becerileri"
              items={maarifData.okuryazarlikBecerileri}
              color="teal"
              icon={BookOpen}
              isSelectable={true}
              isSelected={selectedComponents.okuryazarlikBecerileri}
              onToggle={() => handleComponentToggle("okuryazarlikBecerileri")}
            />
            <SelectableDataSection
              title="Disiplinler Arası İlişkiler"
              items={maarifData.disiplinlerArasiIliskiler}
              color="emerald"
              icon={Globe}
              isSelectable={true}
              isSelected={selectedComponents.disiplinlerArasiIliskiler}
              onToggle={() =>
                handleComponentToggle("disiplinlerArasiIliskiler")
              }
            />
            <SelectableDataSection
              title="Beceriler Arası İlişkiler"
              items={maarifData.becerilerArasiIliskiler}
              color="green"
              icon={Users}
              isSelectable={true}
              isSelected={selectedComponents.becerilerArasiIliskiler}
              onToggle={() => handleComponentToggle("becerilerArasiIliskiler")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UbdMaarifDataStep;
